import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Plus, Search, UserPlus, X } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/blueprints/ui/OSAwareDialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import type { UserConnection } from '@/hooks/useUserMessages';

export interface MessengerLookupResult {
  userId: string;
  messengerId: string;
  displayName: string;
  avatarUrl?: string | null;
}

interface MessengerSidebarToolsProps {
  currentUserId: string;
  connections: UserConnection[];
  onLookupUser: (messengerId: string) => Promise<MessengerLookupResult | null>;
  onRequestConnection: (targetUserId: string, displayName: string) => Promise<boolean>;
  onUpdateConnectionStatus: (connectionId: string, status: 'accepted' | 'declined') => Promise<void>;
}

const getInitials = (value?: string | null) => {
  if (!value) {
    return 'U';
  }

  return value
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const getConnectionWithUser = (connections: UserConnection[], currentUserId: string, otherUserId: string) =>
  connections.find(
    (connection) =>
      (connection.user_id === currentUserId && connection.connected_user_id === otherUserId) ||
      (connection.connected_user_id === currentUserId && connection.user_id === otherUserId)
  ) ?? null;

export default function MessengerSidebarTools({
  currentUserId,
  connections,
  onLookupUser,
  onRequestConnection,
  onUpdateConnectionStatus,
}: MessengerSidebarToolsProps) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lookupInput, setLookupInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState<MessengerLookupResult | null>(null);

  useEffect(() => {
    if (dialogOpen) {
      return;
    }

    setLookupInput('');
    setLookupResult(null);
    setLookupLoading(false);
    setRequestLoading(false);
  }, [dialogOpen]);

  const pendingIncomingConnections = useMemo(
    () => connections.filter((connection) => connection.status === 'pending' && connection.connected_user_id === currentUserId),
    [connections, currentUserId]
  );

  const matchedConnection = lookupResult
    ? getConnectionWithUser(connections, currentUserId, lookupResult.userId)
    : null;

  const lookupState = !matchedConnection
    ? 'none'
    : matchedConnection.status === 'accepted'
      ? 'accepted'
      : matchedConnection.status === 'pending'
        ? matchedConnection.connected_user_id === currentUserId
          ? 'pending-incoming'
          : 'pending-outgoing'
        : 'none';

  const handleLookup = async () => {
    const normalized = lookupInput.replace(/\D/g, '').slice(0, 12);

    if (!normalized) {
      toast({
        title: 'Enter a Messenger ID',
        description: 'Ask your friend for their numeric Messenger ID.',
        variant: 'destructive',
      });
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);

    try {
      const result = await onLookupUser(normalized);
      setLookupResult(result);

      if (!result) {
        toast({
          title: 'No user found',
          description: 'Check the Messenger ID and try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSendRequest = async () => {
    if (!lookupResult) {
      return;
    }

    setRequestLoading(true);

    try {
      const sent = await onRequestConnection(lookupResult.userId, lookupResult.displayName);
      if (sent) {
        setDialogOpen(false);
      }
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="mb-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Friends</p>
          <p className="text-[11px] text-muted-foreground">Add people with a shared Messenger ID only.</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => setDialogOpen(true)}
          aria-label="Add friend by Messenger ID"
          title="Add friend"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {pendingIncomingConnections.length > 0 && (
        <div className="rounded-xl border border-border/70 bg-card/70 p-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">Pending requests</p>
            <Badge variant="secondary">{pendingIncomingConnections.length}</Badge>
          </div>
          <div className="space-y-2">
            {pendingIncomingConnections.map((connection) => (
              <div key={connection.id} className="rounded-lg border border-border/60 bg-background/60 p-2.5">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9 border border-border/50">
                    <AvatarImage src={connection.connected_user_avatar} alt={connection.connected_user_name || 'User'} />
                    <AvatarFallback>{getInitials(connection.connected_user_name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {connection.connected_user_name || 'User'}
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">Wants to add you as a friend</p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="flex-1 gap-1.5" onClick={() => onUpdateConnectionStatus(connection.id, 'accepted')}>
                    <Check className="h-4 w-4" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1.5"
                    onClick={() => onUpdateConnectionStatus(connection.id, 'declined')}
                  >
                    <X className="h-4 w-4" />
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog embedded open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent embedded className="sm:max-w-md">
          <DialogHeader embedded>
            <DialogTitle embedded>Add friend</DialogTitle>
            <DialogDescription embedded>
              Enter the exact numeric Messenger ID someone shared with you.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={lookupInput}
                onChange={(event) => setLookupInput(event.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="Enter Messenger ID"
                aria-label="Messenger ID"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleLookup();
                  }
                }}
              />
              <Button type="button" variant="outline" className="gap-2" onClick={handleLookup} disabled={lookupLoading}>
                {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Lookup
              </Button>
            </div>

            {lookupResult && (
              <div className="rounded-xl border border-border/70 bg-card/70 p-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-11 w-11 border border-border/50">
                    <AvatarImage src={lookupResult.avatarUrl || undefined} alt={lookupResult.displayName} />
                    <AvatarFallback>{getInitials(lookupResult.displayName)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-foreground">{lookupResult.displayName}</p>
                      <Badge variant="outline" className="font-mono text-[10px] tracking-[0.16em]">
                        {lookupResult.messengerId}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {lookupState === 'accepted'
                        ? 'You are already friends.'
                        : lookupState === 'pending-incoming'
                          ? 'This person already sent you a friend request.'
                          : lookupState === 'pending-outgoing'
                            ? 'Your friend request is already pending.'
                            : 'This user can be added to your friends list.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter embedded className="gap-2 sm:space-x-0">
            {lookupResult && lookupState === 'pending-incoming' ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => matchedConnection && onUpdateConnectionStatus(matchedConnection.id, 'declined')}
                >
                  Decline
                </Button>
                <Button type="button" onClick={() => matchedConnection && onUpdateConnectionStatus(matchedConnection.id, 'accepted')}>
                  Accept request
                </Button>
              </>
            ) : lookupState === 'accepted' ? (
              <Button type="button" disabled>
                Already friends
              </Button>
            ) : lookupState === 'pending-outgoing' ? (
              <Button type="button" disabled>
                Request pending
              </Button>
            ) : (
              <Button type="button" className="gap-2" onClick={handleSendRequest} disabled={!lookupResult || requestLoading}>
                {requestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Send request
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}